import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  ArrowRight, 
  Database,
  RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';
import { adminAuthService } from '../../../shared/services/adminAuth';

interface BackupFile {
  id: string;
  filename: string;
  size: number;
  sizeFormatted: string;
  createdAt: string;
  type: 'manual' | 'daily' | 'weekly' | 'monthly' | 'pre-restore';
  status: 'success' | 'failed' | 'in_progress';
}

interface RestoreStep {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  active: boolean;
}

interface RestoreWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedBackup: BackupFile | null;
  onRestoreComplete: () => void;
}

const RestoreWizardModal: React.FC<RestoreWizardModalProps> = ({
  isOpen,
  onClose,
  selectedBackup,
  onRestoreComplete
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [restoreOptions, setRestoreOptions] = useState({
    createPreBackup: true,
    verifyAfterRestore: true,
    restoreType: 'full' as 'full' | 'selective'
  });
  const [confirmationText, setConfirmationText] = useState('');

  const steps: RestoreStep[] = [
    {
      id: 1,
      title: '백업 선택',
      description: '복원할 백업 파일을 확인하세요',
      completed: currentStep > 1,
      active: currentStep === 1
    },
    {
      id: 2,
      title: '복원 옵션',
      description: '복원 방식과 안전 옵션을 설정하세요',
      completed: currentStep > 2,
      active: currentStep === 2
    },
    {
      id: 3,
      title: '안전성 확인',
      description: '복원의 위험성을 이해하고 확인하세요',
      completed: currentStep > 3,
      active: currentStep === 3
    }
  ];

  if (!isOpen || !selectedBackup) {return null;}

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Shield className="h-6 w-6 text-blue-600 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  안전한 백업 복원
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  단계별 안전 절차를 통한 데이터베이스 복원
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                    step.completed 
                      ? 'bg-green-100 border-green-500 text-green-600'
                      : step.active 
                        ? 'bg-blue-100 border-blue-500 text-blue-600'
                        : 'bg-gray-100 border-gray-300 text-gray-400'
                  }`}>
                    {step.completed ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <span className="text-sm font-medium">{step.id}</span>
                    )}
                  </div>
                  <div className="ml-3 hidden sm:block">
                    <p className={`text-sm font-medium ${
                      step.active ? 'text-blue-600' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-400">{step.description}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <ArrowRight className="h-4 w-4 text-gray-300 mx-4" />
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Step Content */}
          <div className="p-6">
            {/* Step 1: 백업 선택 */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    선택된 백업 파일 확인
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center">
                      <Database className="h-8 w-8 text-blue-600 mr-4" />
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {selectedBackup.filename}
                        </h4>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          <span>크기: {selectedBackup.sizeFormatted}</span>
                          <span className="mx-2">•</span>
                          <span>생성일: {new Date(selectedBackup.createdAt).toLocaleString()}</span>
                          <span className="mx-2">•</span>
                          <span>유형: {selectedBackup.type}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-4">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-yellow-400 mr-3 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        중요한 안내사항
                      </h4>
                      <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                        <ul className="list-disc pl-5 space-y-1">
                          <li>복원 과정에서 <strong>현재 모든 데이터가 선택한 백업으로 교체</strong>됩니다</li>
                          <li>복원 후에는 백업 생성 이후의 모든 변경사항이 <strong>영구적으로 손실</strong>됩니다</li>
                          <li>복원 과정은 <strong>되돌릴 수 없으며</strong>, 완료 후 취소할 수 없습니다</li>
                          <li>안전을 위해 복원 전 현재 데이터의 <strong>자동 백업을 강력히 권장</strong>합니다</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 2: 복원 옵션 */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    복원 옵션 설정
                  </h3>
                  
                  <div className="space-y-4">
                    {/* 복원 전 백업 생성 */}
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="createPreBackup"
                          type="checkbox"
                          checked={restoreOptions.createPreBackup}
                          onChange={(e) => setRestoreOptions({
                            ...restoreOptions,
                            createPreBackup: e.target.checked
                          })}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                      <div className="ml-3">
                        <label htmlFor="createPreBackup" className="text-sm font-medium text-gray-900 dark:text-white">
                          복원 전 현재 데이터 백업 생성 (강력 권장)
                        </label>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          복원 실패 시 현재 상태로 되돌릴 수 있도록 안전 백업을 생성합니다
                        </p>
                      </div>
                    </div>

                    {/* 복원 후 검증 */}
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="verifyAfterRestore"
                          type="checkbox"
                          checked={restoreOptions.verifyAfterRestore}
                          onChange={(e) => setRestoreOptions({
                            ...restoreOptions,
                            verifyAfterRestore: e.target.checked
                          })}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                      <div className="ml-3">
                        <label htmlFor="verifyAfterRestore" className="text-sm font-medium text-gray-900 dark:text-white">
                          복원 후 데이터 무결성 검증
                        </label>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          복원 완료 후 데이터베이스 상태를 검증하여 정상적으로 복원되었는지 확인합니다
                        </p>
                      </div>
                    </div>
                  </div>

                  {!restoreOptions.createPreBackup && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                      <div className="flex">
                        <AlertTriangle className="h-5 w-5 text-red-400 mr-3" />
                        <div>
                          <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                            위험: 복원 전 백업 미설정
                          </h4>
                          <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                            복원 전 백업을 생성하지 않으면 복원 실패 시 현재 데이터를 복구할 수 없습니다.
                            안전을 위해 반드시 체크해주세요.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: 안전성 확인 */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    복원 위험성 이해 및 최종 확인
                  </h3>
                  
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-6">
                    <div className="flex">
                      <AlertTriangle className="h-6 w-6 text-red-500 mr-4 mt-1" />
                      <div className="flex-1">
                        <h4 className="text-lg font-medium text-red-800 dark:text-red-200 mb-3">
                          ⚠️ 데이터베이스 복원 위험 고지
                        </h4>
                        <div className="text-sm text-red-700 dark:text-red-300 space-y-2">
                          <p><strong>복원 시 영향받는 데이터:</strong></p>
                          <ul className="list-disc pl-5 space-y-1">
                            <li>모든 사용자 계정 및 인증 정보</li>
                            <li>모든 단축 URL 및 클릭 통계</li>
                            <li>폴더, 태그 등 모든 북마크 데이터</li>
                            <li>보안 설정 및 로그</li>
                            <li>결제 내역 및 구독 정보</li>
                          </ul>
                          <p className="mt-3 font-semibold">위 모든 데이터가 선택한 백업 시점의 상태로 되돌아갑니다.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
                    <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                      복원 계획 요약
                    </h4>
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      <p><strong>백업 파일:</strong> {selectedBackup.filename}</p>
                      <p><strong>백업 생성일:</strong> {new Date(selectedBackup.createdAt).toLocaleString()}</p>
                      <p><strong>복원 전 백업:</strong> {restoreOptions.createPreBackup ? '생성함' : '생성 안함'}</p>
                      <p><strong>복원 후 검증:</strong> {restoreOptions.verifyAfterRestore ? '수행함' : '수행 안함'}</p>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="confirmation" className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                      위험성을 이해했음을 확인하기 위해 다음 텍스트를 정확히 입력하세요:
                    </label>
                    <div className="bg-gray-100 dark:bg-gray-700 rounded p-3 mb-3">
                      <code className="text-sm font-mono text-gray-900 dark:text-white">
                        복원 위험성을 이해하고 진행합니다
                      </code>
                    </div>
                    <input
                      id="confirmation"
                      type="text"
                      value={confirmationText}
                      onChange={(e) => setConfirmationText(e.target.value)}
                      placeholder="위 텍스트를 정확히 입력하세요"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                이전
              </button>
              
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  취소
                </button>
                
                {currentStep < 3 ? (
                  <button
                    onClick={() => setCurrentStep(Math.min(3, currentStep + 1))}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                  >
                    다음
                  </button>
                ) : (
                  <RestoreExecuteButton 
                    disabled={confirmationText !== '복원 위험성을 이해하고 진행합니다'}
                    selectedBackup={selectedBackup}
                    restoreOptions={restoreOptions}
                    onSuccess={() => {
                      onRestoreComplete();
                      onClose();
                    }}
                    onError={(error: string) => {
                      toast.error(error);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

/**
 * 복원 실행 버튼 컴포넌트
 */
interface RestoreExecuteButtonProps {
  disabled: boolean;
  selectedBackup: BackupFile;
  restoreOptions: any;
  onSuccess: () => void;
  onError: (error: string) => void;
}

const RestoreExecuteButton: React.FC<RestoreExecuteButtonProps> = ({
  disabled,
  selectedBackup,
  restoreOptions,
  onSuccess,
  onError
}) => {
  const [isRestoring, setIsRestoring] = useState(false);
  const [progress, setProgress] = useState(0);

  const executeRestore = async () => {
    if (disabled) {
      onError('확인 텍스트가 정확하지 않습니다');
      return;
    }

    setIsRestoring(true);
    setProgress(0);

    try {
      const token = adminAuthService.getToken();
      if (!token) {
        throw new Error('관리자 인증이 필요합니다');
      }

      // 복원 진행률 시뮬레이션
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 500);

      const response = await fetch('/admin/backup/restore', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filename: selectedBackup.filename,
          options: restoreOptions
        })
      });

      clearInterval(progressInterval);
      setProgress(100);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}: 복원 실패`);
      }

      if (data.success) {
        toast.success('백업 복원이 성공적으로 완료되었습니다!', {
          duration: 5000,
          icon: '✅'
        });
        
        // 복원 결과 정보 표시
        if (data.result?.preBackupCreated) {
          toast.success(`안전 백업 생성됨: ${data.result.preBackupCreated}`, {
            duration: 4000
          });
        }
        
        if (data.result?.verification?.success) {
          toast.success(`데이터 검증 완료 (점수: ${data.result.verification.score}%)`, {
            duration: 4000
          });
        }

        onSuccess();
      } else {
        throw new Error(data.message || '복원에 실패했습니다');
      }
    } catch (error: any) {
      console.error('복원 실행 실패:', error);
      
      let errorMessage = '복원 중 오류가 발생했습니다';
      if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        duration: 8000,
        icon: '❌'
      });
      
      onError(errorMessage);
    } finally {
      setIsRestoring(false);
      setProgress(0);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={executeRestore}
        disabled={disabled || isRestoring}
        className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
      >
        {isRestoring ? (
          <>
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            복원 중... ({Math.round(progress)}%)
          </>
        ) : (
          '복원 실행'
        )}
      </button>
      
      {/* 진행률 바 */}
      {isRestoring && (
        <div className="absolute top-full left-0 right-0 mt-2">
          <div className="bg-gray-200 rounded-full h-2">
            <div
              className="bg-red-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1 text-center">
            데이터베이스 복원 중... 잠시만 기다려주세요
          </p>
        </div>
      )}
    </div>
  );
};

export default RestoreWizardModal;